import { AuthInstance, MediaQuality, Screen } from '@signage-player/data-store';
import { AxiosWrapper } from '@signage-player/shared-logic';
import { CacheManager, CacheStoreTag, MemoryCacheStore } from '@signage-player/cache';
import {
  BroadcastInterface,
  DisplayPlayerManagerInterface,
  HealthcheckManagerInterface,
  HealthcheckType,
  LifecycleInterface,
  LoggerInterface,
  MediaControlInterface,
  MultiInstanceManagerInterface,
  ProcessActionServiceInterface,
  ScreenServiceInterface,
  ScreenVendorManagerInterface,
  LogLevel,
} from '@signage-player/contracts';
import { ConsoleLogger, LogManager, SentryLogger } from '@signage-player/logger';
import { HealthcheckManager } from '@signage-player/monitoring';
import { ScreenVendorManager } from '@signage-player/screen-vendor';
import { LocalStorageCacheStore } from '@signage-player/cache-browser';
import { DeviceService, ProcessActionService, resolveInstanceId } from '@signage-player/device-browser';
import { IndexedDBLogger, PostHogLogger } from '@signage-player/logger-browser';
import { LocalMediaControl } from '@signage-player/media-control-browser';
import { StubHealthcheck } from '@signage-player/monitoring-browser';
import { AuthService, ServerApiService } from '@signage-player/api';
import {
  SignageApplication, ServiceContainerKey, AbstractApp, AbstractServiceContainer, Platform,
} from '@signage-player/core';
import { ConfigService } from './services/ConfigService';
import { WebBroadcast } from './services/WebBroadcast';
import { WebLifecycle } from './services/WebLifecycle';
import { WebDisplayPlayerManager } from './services/WebDisplayPlayerManager';
import { WebPlayerManagerFactory } from './services/WebPlayerManagerFactory';
import { WebSignageApplication } from './WebSignageApplication';
import { applyDeviceRotation, effectiveViewport, rotationToOrientation } from './webRotation';

// Adapter names — string literals match the @signage-player/contracts log levels
// and the values used in VITE_LOGGER_ADAPTERS / cache adapter names.
const ADAPTER_CONSOLE = 'console';
const ADAPTER_INDEXEDDB = 'indexeddb';
const ADAPTER_SENTRY = 'sentry';
const ADAPTER_POSTHOG = 'posthog';
const CACHE_ADAPTER_MEMORY = 'memory';
const CACHE_ADAPTER_LOCALSTORAGE = 'localStorage';

/**
 * Browser-flavour service container. Mirrors apps/electron/src/main/ServiceContainer.ts
 * but substitutes browser-compatible packages for every Electron/Node dependency.
 */
export class WebServiceContainer extends AbstractServiceContainer {
  public build(): WebServiceContainer {
    this.register(ServiceContainerKey.Config, () => new ConfigService());
    this.register(ServiceContainerKey.Broadcast, () => new WebBroadcast());

    const config = this.resolve<ConfigService>(ServiceContainerKey.Config);
    const broadcast = this.resolve<BroadcastInterface>(ServiceContainerKey.Broadcast);

    // LogManager + adapters. Mirrors apps/electron/src/main/services/Factory/LoggerFactory.ts.
    const logManager = new LogManager(LogLevel.DEBUG);
    const adapters = config.logging.adapters;
    for (const [index, adapter] of adapters.entries()) {
      const level = config.logging.levels[index] || LogLevel.INFO;
      switch (adapter) {
        case ADAPTER_CONSOLE:
          logManager.registerChannel(ADAPTER_CONSOLE, new ConsoleLogger(level, broadcast), true);
          break;
        case ADAPTER_INDEXEDDB:
          // 100 MB to match electron's FileLogger budget; the 10 MB default
          // thrashes because storage.estimate() counts the whole origin.
          logManager.registerChannel(
            ADAPTER_INDEXEDDB,
            new IndexedDBLogger(level, { filePrefix: 'app', maxTotalSize: 100 * 1024 * 1024 }),
            true,
          );
          break;
        case ADAPTER_SENTRY:
          logManager.registerChannel(ADAPTER_SENTRY, new SentryLogger(level, config), true);
          break;
        case ADAPTER_POSTHOG:
          logManager.registerChannel(ADAPTER_POSTHOG, new PostHogLogger(level, config), true);
          break;
        default:
          console.warn(`[WebServiceContainer] Unknown logger adapter: ${adapter}`);
      }
    }
    // Fallback: if VITE_LOGGER_ADAPTERS didn't include 'console', register one
    // anyway so signage runtime logs (content sync, scheduler, player) are
    // visible during web-port development. Remove or gate behind an env var
    // once the port is stable.
    if (!adapters.includes(ADAPTER_CONSOLE)) {
      logManager.registerChannel(ADAPTER_CONSOLE, new ConsoleLogger(LogLevel.DEBUG, broadcast), true);
      console.log('[WebServiceContainer] console adapter not in VITE_LOGGER_ADAPTERS; auto-attached for visibility');
    }
    this.instance(ServiceContainerKey.Logger, logManager);
    this.instance(ServiceContainerKey.LogManager, logManager);

    // CacheManager. Mirrors apps/electron/src/main/services/Factory/CacheFactory.ts —
    // memory for ephemeral, localStorage for persistent.
    this.register(ServiceContainerKey.CacheManager, () => {
      const logger = this.resolve<LoggerInterface>(ServiceContainerKey.Logger);
      const cacheManager = new CacheManager();
      cacheManager.registerStore(CACHE_ADAPTER_MEMORY, new MemoryCacheStore(), CacheStoreTag.Temp);
      // Namespaced per instance id so tabs (one device each) don't clobber
      // each other's auth/eventList/deviceInfo.
      cacheManager.registerStore(
        CACHE_ADAPTER_LOCALSTORAGE,
        new LocalStorageCacheStore(logger, `sp:${resolveInstanceId()}:`),
        CacheStoreTag.Persistent,
      );
      return cacheManager;
    });

    config.setCacheManager(this.resolve<CacheManager>(ServiceContainerKey.CacheManager));

    // HealthcheckManager. No external watchdog in the browser — stub every type.
    this.register(ServiceContainerKey.HealthcheckManager, () => {
      const manager = new HealthcheckManager();
      manager.registerHealthcheck(HealthcheckType.Up, new StubHealthcheck());
      manager.registerHealthcheck(HealthcheckType.MainLoop, new StubHealthcheck());
      manager.registerHealthcheck(HealthcheckType.DeviceDataReceived, new StubHealthcheck());
      manager.registerHealthcheck(HealthcheckType.Renderer, new StubHealthcheck());
      return manager;
    });

    this.register(ServiceContainerKey.Lifecycle, () => {
      return new WebLifecycle(this.resolve<LoggerInterface>(ServiceContainerKey.Logger));
    });

    this.register(ServiceContainerKey.Device, () => {
      return new DeviceService(
        this.resolve<ConfigService>(ServiceContainerKey.Config),
        this.resolve<LoggerInterface>(ServiceContainerKey.Logger),
        this.resolve<CacheManager>(ServiceContainerKey.CacheManager),
      );
    });

    // ScreenVendor — empty manager (no CEC vendors registered in the browser).
    this.register(ServiceContainerKey.ScreenVendor, () => new ScreenVendorManager());

    this.register(ServiceContainerKey.Auth, () => {
      return new AuthService(
        this.resolve<ConfigService>(ServiceContainerKey.Config),
        this.resolve<LoggerInterface>(ServiceContainerKey.Logger),
        this.resolve<DeviceService>(ServiceContainerKey.Device),
        this.resolve<CacheManager>(ServiceContainerKey.CacheManager),
        broadcast,
      );
    });

    return this;
  }

  public additionalBuild(authInstance: AuthInstance): WebServiceContainer {
    const logger = this.resolve<LoggerInterface>(ServiceContainerKey.Logger);
    const config = this.resolve<ConfigService>(ServiceContainerKey.Config);
    const broadcast = this.resolve<BroadcastInterface>(ServiceContainerKey.Broadcast);
    const lifecycle = this.resolve<LifecycleInterface>(ServiceContainerKey.Lifecycle);

    this.instance(
      ServiceContainerKey.Axios,
      new AxiosWrapper(authInstance.url, logger, authInstance.token ?? undefined, {
        logRequestContext: config.logging.logRequestContext,
        logResponseContext: config.logging.logResponseContext,
      }),
    );
    this.instance(
      ServiceContainerKey.Api,
      new ServerApiService(this.resolve<AxiosWrapper>(ServiceContainerKey.Axios), authInstance),
    );

    const mediaControl = new LocalMediaControl(MediaQuality.P1080, logger, broadcast);
    this.instance(ServiceContainerKey.MediaControl, mediaControl);

    // Corrected wiring: factory first, then manager constructed with (logger, factory).
    const playerManagerFactory = new WebPlayerManagerFactory(
      MediaQuality.P1080, broadcast, logger, mediaControl,
    );
    const player = new WebDisplayPlayerManager(logger, playerManagerFactory);
    this.instance(ServiceContainerKey.Player, player);

    // Single virtual display sized to the browser viewport.
    // Implements the full ScreenServiceInterface — methods the base SignageApplication
    // actually calls are: processDeviceData, getScreens, updateContentImminence.
    // The remaining mutating methods are no-ops (no real hardware to drive).
    const screenStub: ScreenServiceInterface = {
      async processDeviceData(device, _isPeriodically): Promise<void> {
        // The dashboard's display orientation is this port's rotation (webRotation.ts).
        applyDeviceRotation(device.displays);
      },

      async getScreens(_force): Promise<Screen[]> {
        // Report the geometry the content actually gets under that rotation.
        const { width, height } = effectiveViewport();
        return [
          new Screen(
            1,
            'Browser Display',
            0,
            0,
            width,
            height,
            typeof window !== 'undefined' ? window.devicePixelRatio : 1,
            rotationToOrientation(),
            60,
            null,
            true,
          ),
        ];
      },

      async findCurrentScreen(_force): Promise<Screen | null> {
        const screens = await screenStub.getScreens(false);
        return screens[0] ?? null;
      },

      async turnOnScreens(_screens, _force): Promise<void> {
        // no-op — browser cannot power screens on/off
      },

      async turnOffScreens(_screens, _force): Promise<void> {
        // no-op — browser cannot power screens on/off
      },

      async setScreenOrientation(_screen, _orientation, _force): Promise<void> {
        // no-op — browser orientation is controlled by the OS / device
      },

      async setScreenResolution(_screen, _width, _height, _force): Promise<void> {
        // no-op — browser cannot set display resolution
      },

      isReconfiguringDisplays(): boolean {
        return false;
      },

      async updateContentImminence(_isImminent): Promise<void> {
        // no-op — no screen power management in the browser
      },
    };
    this.instance(ServiceContainerKey.Screen, screenStub);

    this.instance(
      ServiceContainerKey.ProcessAction,
      new ProcessActionService(logger, lifecycle, this.resolve<ServerApiService>(ServiceContainerKey.Api)),
    );

    // No MultiInstanceManager in the web port — single window, single display.
    return this;
  }

  public async initSignageApplication(authInstance: AuthInstance): Promise<SignageApplication> {
    const player = this.resolve<DisplayPlayerManagerInterface>(ServiceContainerKey.Player);
    const logger = this.resolve<LoggerInterface>(ServiceContainerKey.Logger);
    const configService = this.resolve<ConfigService>(ServiceContainerKey.Config);
    const mediaControl = this.resolve<MediaControlInterface>(ServiceContainerKey.MediaControl);
    const axios = this.resolve<AxiosWrapper>(ServiceContainerKey.Axios);
    const screen = this.resolve<ScreenServiceInterface>(ServiceContainerKey.Screen);

    await player.getOrInitializeForDisplay(0);
    await mediaControl.initialize();
    if (!configService.dynamicConfig || Object.keys(configService.dynamicConfig).length === 0) {
      await configService.loadDynamicConfig(axios, authInstance);
    }

    // Mirrors electron main.ts: push server-side overrides into live log
    // channels (positional per local adapter list) and axios options.
    const logManager = this.resolve<LogManager>(ServiceContainerKey.LogManager);
    const dynamicConfig = configService.dynamicConfig;
    if (dynamicConfig?.overrides) {
      if (dynamicConfig.overrides.logging?.levels) {
        for (const [index, adapter] of configService.logging.adapters.entries()) {
          const newLevel = dynamicConfig.overrides.logging.levels[index];
          if (newLevel) {
            logManager.setChannelLevel(adapter, newLevel);
          }
        }
        logger.info('[WebServiceContainer] Log levels updated from dynamic config', {
          levels: dynamicConfig.overrides.logging.levels,
        });
      }
      if (
        dynamicConfig.overrides.logging?.logRequestContext !== undefined ||
        dynamicConfig.overrides.logging?.logResponseContext !== undefined
      ) {
        axios.setLogOptions({
          logRequestContext: configService.logging.logRequestContext,
          logResponseContext: configService.logging.logResponseContext,
        });
        logger.info('[WebServiceContainer] Axios log options updated from dynamic config');
      }
    }

    const signageApp = new WebSignageApplication(
      logger,
      configService,
      player,
      mediaControl,
      this.resolve<CacheManager>(ServiceContainerKey.CacheManager),
      authInstance,
      this.resolve<ServerApiService>(ServiceContainerKey.Api),
      this.resolve<LifecycleInterface>(ServiceContainerKey.Lifecycle),
      this.resolve<DeviceService>(ServiceContainerKey.Device),
      screen,
      null as MultiInstanceManagerInterface | null,
      this.resolve<ProcessActionServiceInterface>(ServiceContainerKey.ProcessAction),
      this.resolve<ScreenVendorManagerInterface>(ServiceContainerKey.ScreenVendor),
      this.resolve<HealthcheckManagerInterface>(ServiceContainerKey.HealthcheckManager),
      Platform.Web,
    );
    signageApp.initialize();
    this.instance(ServiceContainerKey.SignageApplication, signageApp);
    return signageApp;
  }
}

let globalContainer: WebServiceContainer | null = null;

export function getContainer(): WebServiceContainer {
  if (!globalContainer) globalContainer = new WebServiceContainer();
  return globalContainer;
}

export class App extends AbstractApp {
  protected static serviceContainer = getContainer();

  public static container(): WebServiceContainer {
    return this.serviceContainer;
  }
}
